import { useState, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ModalContext } from '../ModalContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { useGetAthletes } from 'redux/customHooks/useGetAthletes'
import { useGetCoaches } from 'redux/customHooks/useGetCoaches'
import { updateOrganizationAthleteAssignedTrainerId } from 'redux/slices/organization'

import MediumLoadingComponent from 'components/Loading/MediumLoadingComponent'
import OutlineButton from 'components/Buttons/OutlineButton'
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function AssignTrainerModal() {

    const dispatch = useDispatch()

    const { modalData, HideModal } = useContext(ModalContext)
    
    const athletes = useGetAthletes()
    const coaches = useGetCoaches()
    
    const organizationId = useSelector((state) => state.user.organizationId)
    const userId = useSelector((state) => state.user.id)
    
    const [selectedTrainerId, setSelectedTrainerId] = useState(modalData.organizationTrainerId ?? '')
    const [isLoading, setIsLoading] = useState(false)
    const [errorText, setErrorText] = useState('')

    const OnSaveChanges = async () => {
        setErrorText('')
        setIsLoading(true)

        try {
            const jsonBody = {
                organizationId: organizationId,
                coachId: userId,
                athleteId: modalData.athleteId,
                setToUnassigned: selectedTrainerId === 'no-coach',
                organizationTrainerId: selectedTrainerId === 'no-coach' ? 'no-coach' : parseInt(selectedTrainerId)
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('CORE', 'POST', '/update_coach_athlete_assignment', jsonBody)

            // console.log(result)

            setIsLoading(false)

            if (result.data.status === 'failure') {
                console.error(result)
                setErrorText('Failed to set coach assignment. Please try again.')
                return
            }

            const payload = {
                athleteId: modalData.athleteId,
                organizationTrainerId: selectedTrainerId === 'no-coach' ? null : parseInt(selectedTrainerId)
            }

            // console.log(payload)

            dispatch(updateOrganizationAthleteAssignedTrainerId(payload))

            HideModal()

        } catch (error) {
            setIsLoading(false)
            console.error('Error assigning trainer:', error)
            setErrorText('Failed to set coach assignment. Please try again.')
            return
        }
    }

    if (athletes === null || coaches === null) {
        return <MediumLoadingComponent />
    }

    return (
        <div className="space-y-4">
            <div className='-mt-5 space-y-2'>
                <div className="text-left text-base font-semibold leading-6 text-gray-900">
                    Assign {modalData.athleteName} to a Coach
                </div>

                <p className='text-xs text-left text-gray-500'>
                    When an athlete is assigned to a coach, all athlete data will be available to that coach. 
                    Organization owners and admins do not need athletes assigned to them in order to view athlete data.
                </p>
            </div>

            {coaches === null && 
                <div className='w-full h-16 bg-gray-200 rounded-md animate-pulse' />
            }
            
            {coaches !== null && (
                <div className='space-y-2'>
                    <p className='text-xs text-gray-800'>Current Coach</p>
                    <div className='border border-gray-400 rounded-md'>
                        <select
                            className="w-full rounded-md bg-white px-2 py-2 border-r-[10px] border-white focus:outline-none cursor-pointer hover:bg-gray-50 hover:border-gray-50"
                            value={selectedTrainerId}
                            onChange={(e) => setSelectedTrainerId(e.target.value)}
                        >
                            <option value="no-coach">No coach</option>
                            {coaches.map((trainer) => (
                                <option key={trainer.id} value={trainer.id}>
                                    {trainer.firstName} {trainer.lastName} ({trainer.email})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {errorText !== '' && (
                <div className="text-red-500 text-sm text-center">{errorText}</div>
            )}

            <div className='flex items-center gap-x-4'>
                <div className='flex-1'>
                    <OutlineButton
                        text="Cancel"
                        className='hover:bg-gray-50'
                        action={HideModal}
                    />
                </div>

                <div className='flex-1'>
                    <BrandColorButton
                        text="Save Changes"
                        action={OnSaveChanges}
                        isLoading={isLoading}
                    />
                </div>
            </div>
        </div>
    )
}
